import { useSyncExternalStore } from 'react';

/**
 * Asked in a throwaway branch of the conversation's agent session once a reply ends, so the
 * agent predicts with all its context and the provider's prompt cache. No output schema: one
 * would add a tool to the branch and lose that cache.
 */
export const MESSAGE_PREDICTION_PROMPT = `Predict the next message the user will send in this conversation, as if you were the user typing it in the chat input.

- Base it on the conversation so far, above all your last reply: answer its question, pick the option it offered, approve the next step it proposed, or ask the obvious follow-up.
- Write it as the user would: their language, their tone, the way they phrase their messages. Keep it short, one sentence or two at most.
- Do not use any tool, read any file or run anything. Answer from the conversation alone.
- When no next message is clearly likely, abstain.

Return only JSON matching this schema: {"suggestion": string | null}. Use null to abstain.`;

/** The suggestion in the agent's answer, or null when it abstained or answered something else. */
export function predictionFromOutput(output: string): string | null {
  const json = output.slice(output.indexOf('{'), output.lastIndexOf('}') + 1);
  try {
    const parsed: unknown = JSON.parse(json);
    if (parsed === null || typeof parsed !== 'object') return null;
    const suggestion = (parsed as { suggestion?: unknown }).suggestion;
    return typeof suggestion === 'string' && suggestion.trim() !== '' ? suggestion.trim() : null;
  } catch {
    return null;
  }
}

type Prediction = { afterMessageId: string; text: string | null; abort: AbortController };

const predictions = new Map<string, Prediction>();
const listeners = new Set<() => void>();

function changed(): void {
  for (const listener of listeners) listener();
}

/**
 * Predicts the message that follows `afterMessageId`, the reply that just ended. A newer
 * request for the conversation cancels the one in flight.
 */
export function requestMessagePrediction(
  conversationId: string,
  afterMessageId: string,
  predict: (abortSignal: AbortSignal) => Promise<string | null>,
): void {
  predictions.get(conversationId)?.abort.abort();
  const prediction: Prediction = { afterMessageId, text: null, abort: new AbortController() };
  predictions.set(conversationId, prediction);
  void predict(prediction.abort.signal)
    .then((text) => {
      if (predictions.get(conversationId) !== prediction || text === null) return;
      prediction.text = text;
      changed();
    })
    .catch(() => undefined);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The predicted next message, while the reply it follows is still the conversation's latest. */
export function useMessagePrediction(
  conversationId: string | null,
  latestMessageId: string | null,
): string | null {
  return useSyncExternalStore(subscribe, () => {
    if (conversationId === null || latestMessageId === null) return null;
    const prediction = predictions.get(conversationId);
    return prediction?.afterMessageId === latestMessageId ? prediction.text : null;
  });
}
